import { useEffect, useState } from 'react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyAgentRentals, type AgentRentalsResponse } from '@/lib/collections/agentRentals';

export interface ActiveAgentRentalState {
  /** The renter's active (unexpired) rental for this agent, if any. */
  rental: AgentRentalsResponse | null;
  /** Live-updating seconds remaining until `rental.expiresAt`. Ticks every 1s. */
  secondsRemaining: number;
  loading: boolean;
}

/**
 * Subscribes to `agentRentals` filtered to this renter + agent, and returns
 * the rental with the latest `expiresAt` that is still in the future — i.e.
 * the currently-active unlimited-access window, if any. `secondsRemaining`
 * ticks every second (mirrors the GPU rental countdown pattern) so callers
 * can render a live countdown and re-check activity at send time (rentals
 * naturally fall out of `rental` once `expiresAt` passes `now`).
 */
export function useActiveAgentRental(
  agentId: string | undefined | null,
  address: string | undefined | null
): ActiveAgentRentalState {
  const enabled = !!agentId && !!address;
  const filter = enabled ? `agentId='${agentId}' and renterWallet='${address}' limit 50` : '';

  const { data: rentals, loading } = useRealtimeData<AgentRentalsResponse[]>(
    subscribeManyAgentRentals,
    enabled,
    filter
  );

  const [nowSeconds, setNowSeconds] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNowSeconds(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const activeRentals = (rentals ?? []).filter((r) => r.expiresAt > nowSeconds);
  const rental =
    activeRentals.length > 0
      ? activeRentals.reduce((latest, r) => (r.expiresAt > latest.expiresAt ? r : latest))
      : null;

  const secondsRemaining = rental ? Math.max(0, rental.expiresAt - nowSeconds) : 0;

  return { rental, secondsRemaining, loading };
}

export default useActiveAgentRental;
